import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminOrders, type OrderAdmin } from '../../api/admin';
import { products as productsApi, coupons as couponsApi, type ProductAPI } from '../../api/client';
import {
  ShoppingCart, Search, ChevronDown, ChevronUp, Save, X,
  MapPin, Phone, Mail, Package, CheckCircle, Truck, XCircle, Clock, Edit3, Trash2, Plus,
  ExternalLink, Globe, Smartphone, Monitor, Tablet, Eye, RotateCcw, FileSpreadsheet, Check, AlertCircle,
} from 'lucide-react';
import { FREE_SHIPPING_THRESHOLD, FLAT_SHIPPING_FEE } from '../../lib/shipping';

const STATUS_OPTIONS = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled', 'returned'] as const;

const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string; icon: React.ElementType }> = {
  pending:   { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30', icon: Clock },
  confirmed: { bg: 'bg-blue-500/10',   text: 'text-blue-500',   border: 'border-blue-500/30',   icon: CheckCircle },
  shipped:   { bg: 'bg-purple-500/10',  text: 'text-purple-500', border: 'border-purple-500/30',  icon: Truck },
  delivered: { bg: 'bg-emerald-500/10', text: 'text-emerald-500',border: 'border-emerald-500/30', icon: CheckCircle },
  cancelled: { bg: 'bg-red-500/10',     text: 'text-red-500',    border: 'border-red-500/30',     icon: XCircle },
  returned:  { bg: 'bg-orange-500/10',  text: 'text-orange-500', border: 'border-orange-500/30',  icon: RotateCcw },
};

/**
 * Count a customer's OTHER orders (same email OR same 10+ digit phone, excluding
 * `order` itself) by status. Shared by the Export-to-Sheets dialog and the
 * per-row history column so both stay in sync.
 */
function getCustomerHistory(order: OrderAdmin, allOrders: OrderAdmin[]) {
  const email = order.ship_email?.toLowerCase();
  const phone = order.ship_phone?.replace(/\D/g, '');
  const userOrders = allOrders.filter(o => {
    if (o.id === order.id) return false;
    const sameEmail = email && o.ship_email?.toLowerCase() === email;
    const samePhone = phone && phone.length >= 10 && o.ship_phone?.replace(/\D/g, '') === phone;
    return sameEmail || samePhone;
  });
  return {
    cancelled: userOrders.filter(o => o.status === 'cancelled').length,
    returned:  userOrders.filter(o => o.status === 'returned').length,
    delivered: userOrders.filter(o => o.status === 'delivered').length,
  };
}

// null = still loading; 'invalid' = expired/exhausted; object = valid coupon ready for live calc
type CouponCache = {
  minOrderValue: number;
  discountType: string;
  discountValue: number;
  maxDiscount: number | null;
} | null | 'invalid';

type EditingOrder = {
  ship_first_name: string;
  ship_last_name: string;
  ship_email: string;
  ship_phone: string;
  ship_address: string;
  ship_city: string;
  ship_state: string;
  ship_pincode: string;
  tracking_id: string;
  tracking_url: string;
  items: { product_id: string | null; product_name: string; unit_price: number; quantity: number }[];
};

export default function OrdersPage() {
  const { isDark } = useTheme();
  const [orders, setOrders] = useState<OrderAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditingOrder | null>(null);
  const [saving, setSaving] = useState(false);
  const [allProducts, setAllProducts] = useState<ProductAPI[]>([]);
  const [pickerOpenIdx, setPickerOpenIdx] = useState<number | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerBrand, setPickerBrand] = useState<string>('all');
  const pickerRef = useRef<HTMLDivElement>(null);
  const dlgPickerRef = useRef<HTMLDivElement>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string>('');
  const [exportDialogOrder, setExportDialogOrder] = useState<OrderAdmin | null>(null);
  const [exportDialogItems, setExportDialogItems] = useState<{ product_id: string | null; product_name: string; unit_price: number; quantity: number }[]>([]);
  const [dlgPickerOpenIdx, setDlgPickerOpenIdx] = useState<number | null>(null);
  const [dlgPickerSearch, setDlgPickerSearch] = useState('');
  const [dlgPickerBrand, setDlgPickerBrand] = useState('all');
  const [editCouponCache, setEditCouponCache] = useState<CouponCache>(null);
  const [dlgCouponCache, setDlgCouponCache] = useState<CouponCache>(null);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    adminOrders.list()
      .then(data => setOrders(data))
      .catch(err => { console.error(err); setError(err.message || 'Failed to load orders'); })
      .finally(() => setLoading(false));
  }, []);

  const fetchAllProducts = useCallback(async () => {
    try {
      const first = await productsApi.list({ page: 1, per_page: 200 });
      let items = first.items;
      // If there are more pages, fetch them too
      if (first.pages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: first.pages - 1 }, (_, i) =>
            productsApi.list({ page: i + 2, per_page: 200 })
          )
        );
        for (const page of remaining) items = items.concat(page.items);
      }
      setAllProducts(items);
    } catch (err) { console.error('Failed to load products:', err); }
  }, []);

  const filtered = useMemo(() => {
    let list = [...orders];
    if (search) {
      const q = search.toLowerCase().trim();
      const digitQ = q.replace(/\D/g, '');
      list = list.filter(o => {
        const fullName = `${o.ship_first_name ?? ''} ${o.ship_last_name ?? ''}`.toLowerCase().trim();
        return (
          fullName.includes(q) ||
          o.ship_first_name?.toLowerCase().includes(q) ||
          o.ship_last_name?.toLowerCase().includes(q) ||
          o.ship_email?.toLowerCase().includes(q) ||
          (digitQ.length > 0 && o.ship_phone?.replace(/\D/g, '').includes(digitQ)) ||
          o.id?.toLowerCase().startsWith(q)
        );
      });
    }
    if (statusFilter !== 'all') list = list.filter(o => o.status === statusFilter);
    return list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [orders, search, statusFilter]);

  const updateStatus = async (orderId: string, status: string) => {
    setUpdatingId(orderId);
    try {
      await adminOrders.updateStatus(orderId, status);
      // Re-fetch full list so all derived data (total, items, etc.) stays in sync
      const freshOrders = await adminOrders.list();
      setOrders(freshOrders);
    } catch (err) { console.error(err); }
    finally { setUpdatingId(null); }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    for (const id of Array.from(selectedIds)) { await updateStatus(id, bulkStatus); }
    setSelectedIds(new Set());
    setBulkStatus('');
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(o => o.id)));
  };

  const startEditing = (order: OrderAdmin) => {
    setEditCouponCache(null); // null = loading — price summary uses stored values until API responds
    setSaveError('');
    setEditingId(order.id);
    setExpandedId(order.id);
    setEditForm({
      ship_first_name: order.ship_first_name,
      ship_last_name: order.ship_last_name,
      ship_email: order.ship_email,
      ship_phone: order.ship_phone,
      ship_address: order.ship_address,
      ship_city: order.ship_city,
      ship_state: order.ship_state,
      ship_pincode: order.ship_pincode,
      tracking_id: order.tracking_id || '',
      tracking_url: order.tracking_url || '',
      items: order.items?.map(i => ({
        product_id: i.product_id ?? (i as any).productId ?? null,
        product_name: i.product_name ?? (i as any).productName ?? '',
        unit_price: i.unit_price ?? (i as any).unitPrice ?? 0,
        quantity: i.quantity,
      })) ?? [],
    });
    // Fetch full coupon data once — pass a huge subtotal so the min-order check
    // doesn't block us; the IIFE handles min-order locally using minOrderValue.
    // 'invalid' is reserved for truly dead coupons (expired / exhausted / not found).
    const orderCouponCode = order.coupon_code ?? (order as any).couponCode ?? null;
    if (orderCouponCode) {
      couponsApi.validate(orderCouponCode, 9_999_999)
        .then(res => {
          if (res.valid && res.coupon) {
            const c = res.coupon as any;
            setEditCouponCache({
              minOrderValue: c.minOrderValue ?? c.min_order_value ?? 0,
              discountType: c.discountType ?? c.discount_type,
              discountValue: c.discountValue ?? c.discount_value,
              maxDiscount: c.maxDiscount ?? c.max_discount ?? null,
            });
          } else {
            setEditCouponCache('invalid');
          }
        })
        .catch(() => setEditCouponCache('invalid'));
    }
    if (allProducts.length === 0) fetchAllProducts();
  };

  const cancelEditing = () => { setEditingId(null); setEditForm(null); setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all'); setEditCouponCache(null); setSaveError(''); };

  const openExportDialog = (order: OrderAdmin) => {
    setDlgCouponCache(null); // null = loading
    setExportDialogOrder(order);
    setExportDialogItems(order.items?.map(i => ({
      product_id: i.product_id ?? (i as any).productId ?? null,
      product_name: i.product_name ?? (i as any).productName ?? '',
      unit_price: i.unit_price ?? (i as any).unitPrice ?? 0,
      quantity: i.quantity,
    })) ?? []);
    setDlgPickerOpenIdx(null); setDlgPickerSearch(''); setDlgPickerBrand('all');
    setExportError('');
    // Fetch full coupon data once — pass a huge subtotal so the min-order check
    // doesn't block us; the IIFE handles min-order locally using minOrderValue.
    const dlgOrderCouponCode = order.coupon_code ?? (order as any).couponCode ?? null;
    if (dlgOrderCouponCode) {
      couponsApi.validate(dlgOrderCouponCode, 9_999_999)
        .then(res => {
          if (res.valid && res.coupon) {
            const c = res.coupon as any;
            setDlgCouponCache({
              minOrderValue: c.minOrderValue ?? c.min_order_value ?? 0,
              discountType: c.discountType ?? c.discount_type,
              discountValue: c.discountValue ?? c.discount_value,
              maxDiscount: c.maxDiscount ?? c.max_discount ?? null,
            });
          } else {
            setDlgCouponCache('invalid');
          }
        })
        .catch(() => setDlgCouponCache('invalid'));
    }
    if (allProducts.length === 0) fetchAllProducts();
  };

  const closeExportDialog = () => {
    setExportDialogOrder(null);
    setExportDialogItems([]);
    setDlgPickerOpenIdx(null);
    setExportError('');
  };

  const exportToSheets = async () => {
    if (!exportDialogOrder) return;
    setExportingId(exportDialogOrder.id);
    setExportError('');
    try {
      // Save modified items to DB first — same as saveEditing
      const validItems = exportDialogItems.filter(i => i.product_id);
      if (validItems.length > 0) {
        await adminOrders.update(exportDialogOrder.id, {
          items: validItems.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        });
      }
      // Export to sheet — backend reads the freshly-updated DB items
      await adminOrders.exportToSheets(exportDialogOrder.id);
      // Auto-confirm pending orders on successful export
      if (exportDialogOrder.status === 'pending') {
        await adminOrders.updateStatus(exportDialogOrder.id, 'confirmed');
      }
      // Refresh full list so item changes + exported_to_sheets_at + status are reflected
      const freshOrders = await adminOrders.list();
      setOrders(freshOrders);
      closeExportDialog();
    } catch (err: any) {
      setExportError(err.message || 'Export failed');
    } finally {
      setExportingId(null);
    }
  };

  const openDlgPicker = (idx: number) => {
    setDlgPickerOpenIdx(dlgPickerOpenIdx === idx ? null : idx);
    setDlgPickerSearch(''); setDlgPickerBrand('all');
  };

  const selectDlgProduct = (idx: number, product: ProductAPI) => {
    setExportDialogItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], product_id: product.id, product_name: product.name, unit_price: product.price };
      return items;
    });
    setDlgPickerOpenIdx(null); setDlgPickerSearch(''); setDlgPickerBrand('all');
  };

  const addDlgItem = (product: ProductAPI) => {
    setExportDialogItems(prev => [...prev, { product_id: product.id, product_name: product.name, unit_price: product.price, quantity: 1 }]);
    setDlgPickerOpenIdx(null); setDlgPickerSearch(''); setDlgPickerBrand('all');
  };

  const removeDlgItem = (idx: number) => {
    setExportDialogItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateDlgItemQty = (idx: number, qty: number) => {
    setExportDialogItems(prev => {
      const items = [...prev];
      items[idx] = { ...items[idx], quantity: qty };
      return items;
    });
  };

  // Close product picker on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close dialog product picker on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dlgPickerRef.current && !dlgPickerRef.current.contains(e.target as Node)) {
        setDlgPickerOpenIdx(null); setDlgPickerSearch(''); setDlgPickerBrand('all');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const brands = useMemo(() => {
    const set = new Set(allProducts.map(p => p.brand));
    return Array.from(set).sort();
  }, [allProducts]);

  const filteredProducts = useMemo(() => {
    let list = allProducts;
    if (pickerBrand !== 'all') list = list.filter(p => p.brand === pickerBrand);
    if (pickerSearch) {
      const q = pickerSearch.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || p.flavor.toLowerCase().includes(q));
    }
    return list;
  }, [allProducts, pickerBrand, pickerSearch]);

  const dlgFilteredProducts = useMemo(() => {
    let list = allProducts;
    if (dlgPickerBrand !== 'all') list = list.filter(p => p.brand === dlgPickerBrand);
    if (dlgPickerSearch) {
      const q = dlgPickerSearch.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q) || p.flavor.toLowerCase().includes(q));
    }
    return list;
  }, [allProducts, dlgPickerBrand, dlgPickerSearch]);

  const dlgOrderHistory = useMemo(() => {
    if (!exportDialogOrder) return null;
    return getCustomerHistory(exportDialogOrder, orders);
  }, [exportDialogOrder, orders]);

  const openPicker = (idx: number) => {
    setPickerOpenIdx(pickerOpenIdx === idx ? null : idx);
    setPickerSearch(''); setPickerBrand('all');
  };

  const selectProduct = (idx: number, product: ProductAPI) => {
    if (!editForm) return;
    const items = [...editForm.items];
    items[idx] = { ...items[idx], product_id: product.id, product_name: product.name, unit_price: product.price };
    setEditForm({ ...editForm, items });
    setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all');
  };

  const addNewItem = (product: ProductAPI) => {
    if (!editForm) return;
    setEditForm({
      ...editForm,
      items: [...editForm.items, { product_id: product.id, product_name: product.name, unit_price: product.price, quantity: 1 }],
    });
    setPickerOpenIdx(null); setPickerSearch(''); setPickerBrand('all');
  };

  const saveEditing = async (orderId: string) => {
    if (!editForm) return;
    setSaving(true);
    setSaveError('');
    try {
      const payload: Record<string, unknown> = {
        ship_first_name: editForm.ship_first_name,
        ship_last_name: editForm.ship_last_name,
        ship_email: editForm.ship_email,
        ship_phone: editForm.ship_phone,
        ship_address: editForm.ship_address,
        ship_city: editForm.ship_city,
        ship_state: editForm.ship_state,
        ship_pincode: editForm.ship_pincode,
        tracking_id: editForm.tracking_id || null,
        tracking_url: editForm.tracking_url || null,
      };
      const validItems = editForm.items.filter(i => i.product_id);
      if (validItems.length > 0) {
        payload.items = validItems.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      }
      await adminOrders.update(orderId, payload);
      // Re-fetch full orders list to get accurate data from DB
      const freshOrders = await adminOrders.list();
      setOrders(freshOrders);
      setEditingId(null);
      setEditForm(null);
      setPickerOpenIdx(null);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save order');
    } finally { setSaving(false); }
  };

  const updateEditField = (field: keyof EditingOrder, value: string) => {
    if (!editForm) return;
    setEditForm({ ...editForm, [field]: value });
  };

  const updateEditItem = (idx: number, field: string, value: string | number) => {
    if (!editForm) return;
    const items = [...editForm.items];
    items[idx] = { ...items[idx], [field]: value };
    setEditForm({ ...editForm, items });
  };

  const removeEditItem = (idx: number) => {
    if (!editForm) return;
    setEditForm({ ...editForm, items: editForm.items.filter((_, i) => i !== idx) });
  };

  const fmt = (n: number) => `\u20B9${n?.toLocaleString('en-IN') ?? 0}`;

  const statusCounts = orders.reduce<Record<string, number>>((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1; return acc;
  }, {});

  const inputCls = `${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'} px-2.5 py-1.5 rounded-lg border text-sm outline-none w-full`;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <XCircle size={40} className="text-red-500" />
      <p className="text-red-500 text-sm">{error}</p>
      <button onClick={() => window.location.reload()} className="text-sm text-emerald-500 hover:underline">Retry</button>
    </div>
  );

  return (
    <>
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart size={24} className="text-emerald-500" /> Orders
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {orders.length} total orders &middot; {statusCounts.pending || 0} pending
          </p>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setStatusFilter('all')}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${statusFilter === 'all' ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/20' : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
          All ({orders.length})
        </button>
        {STATUS_OPTIONS.map(s => {
          const cfg = STATUS_CONFIG[s];
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${statusFilter === s ? `${cfg.bg} ${cfg.text} ${cfg.border} shadow-md` : isDark ? 'border-gray-700 text-gray-400 hover:border-gray-500' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)} ({statusCounts[s] || 0})
            </button>
          );
        })}
      </div>

      {/* Search + Bulk */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" placeholder="Search by name, email, mobile, order ID..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors ${isDark ? 'bg-gray-900 border-gray-800 text-white placeholder-gray-500 focus:border-gray-600' : 'bg-white border-gray-200 placeholder-gray-400 focus:border-gray-300'}`} />
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedIds.size} selected</span>
            <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}>
              <option value="">Change status to...</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <button onClick={handleBulkStatusUpdate} disabled={!bulkStatus}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium transition-colors">Apply</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className={`rounded-xl border overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="min-w-[700px]">
        <div className={`grid grid-cols-[auto_2fr_7rem_1fr_1fr_1fr_auto_auto_auto] gap-4 px-4 py-3 text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-gray-900/80 text-gray-500 border-b border-gray-800' : 'bg-gray-50 text-gray-400 border-b border-gray-200'}`}>
          <div className="flex items-center">
            <input type="checkbox" checked={filtered.length > 0 && selectedIds.size === filtered.length} onChange={toggleSelectAll} className="w-4 h-4 rounded accent-emerald-500" />
          </div>
          <div>Customer</div><div></div><div>Order ID</div><div>Status</div><div>Total</div><div>Edit</div><div className="w-6"></div><div className="w-6"></div>
        </div>

        {filtered.length === 0 && (
          <div className={`text-center py-16 ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
            <Package size={48} className={`mx-auto mb-3 ${isDark ? 'text-gray-700' : 'text-gray-200'}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No orders match your filters</p>
          </div>
        )}

        {filtered.map(order => {
          const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
          const isExpanded = expandedId === order.id;
          const isEditing = editingId === order.id;

          const history = getCustomerHistory(order, orders);

          return (
            <div key={order.id} className={`${isDark ? 'bg-gray-900' : 'bg-white'} ${isExpanded ? '' : isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50'} transition-colors`}>
              <div className="grid grid-cols-[auto_2fr_7rem_1fr_1fr_1fr_auto_auto_auto] gap-4 px-4 py-3.5 items-center">
                <div className="flex items-center" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selectedIds.has(order.id)} onChange={() => toggleSelect(order.id)} className="w-4 h-4 rounded accent-emerald-500" />
                </div>
                <div className="cursor-pointer min-w-0" onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                  <p className="font-semibold text-sm truncate">{order.ship_first_name} {order.ship_last_name}</p>
                  <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{order.ship_email}</p>
                </div>
                <div className="text-[11px] leading-snug whitespace-nowrap">
                  <p className={isDark ? 'text-gray-500' : 'text-gray-400'}><span className="inline-block w-[4.75rem]">Returned:</span><span className="font-semibold text-red-500">{history.returned}</span></p>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-400'}><span className="inline-block w-[4.75rem]">Cancelled:</span><span className="font-semibold text-red-500">{history.cancelled}</span></p>
                  <p className={isDark ? 'text-gray-500' : 'text-gray-400'}><span className="inline-block w-[4.75rem]">Delivered:</span><span className="font-semibold text-emerald-500">{history.delivered}</span></p>
                </div>
                <div className="cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                  <p className={`text-xs font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>#{order.id.slice(0, 8)}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    {new Date(order.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <select value={order.status} onChange={e => updateStatus(order.id, e.target.value)} disabled={updatingId === order.id}
                    className={`px-2.5 py-1.5 rounded-lg border text-xs font-semibold outline-none transition-all cursor-pointer ${cfg.bg} ${cfg.text} ${cfg.border} ${updatingId === order.id ? 'opacity-50' : ''}`}>
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <p className="font-bold text-sm">{fmt(order.total)}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{order.items?.length || 0} item(s)</p>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  {isEditing ? (
                    <div className="flex gap-1">
                      <button onClick={() => saveEditing(order.id)} disabled={saving || (editForm?.items.some(i => !i.quantity) ?? false)}
                        className="p-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white transition-colors disabled:opacity-50">
                        <Save size={14} />
                      </button>
                      <button onClick={cancelEditing}
                        className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startEditing(order)}
                      className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                      <Edit3 size={14} />
                    </button>
                  )}
                </div>
                <div className="cursor-pointer p-1 rounded-lg hover:bg-gray-800/20 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : order.id)}>
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  {!isEditing && (order.exported_to_sheets_at ? (
                    <div className="p-1.5" title={`Exported on ${new Date(order.exported_to_sheets_at).toLocaleString('en-IN')}`}>
                      <Check size={14} className="text-green-500 shrink-0" />
                    </div>
                  ) : (
                    <button
                      onClick={() => openExportDialog(order)}
                      disabled={exportingId === order.id}
                      title="Export to Google Sheets"
                      className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'} ${exportingId === order.id ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {exportingId === order.id
                        ? <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        : <FileSpreadsheet size={14} />}
                    </button>
                  ))}
                </div>
              </div>

              {isExpanded && isEditing && saveError && (
                <div className="px-6 py-2 flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border-t border-red-500/20">
                  <AlertCircle size={13} className="shrink-0" /> {saveError}
                </div>
              )}

              {isExpanded && (
                <div className={`border-t px-6 pb-5 pt-4 space-y-5 ${isDark ? 'border-gray-800 bg-gray-950/50' : 'border-gray-100 bg-gray-50/50'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Shipping info */}
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Truck size={13} /> Customer & Shipping
                      </p>
                      {isEditing && editForm ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <input className={inputCls} placeholder="First name" value={editForm.ship_first_name}
                              onChange={e => updateEditField('ship_first_name', e.target.value)} />
                            <input className={inputCls} placeholder="Last name" value={editForm.ship_last_name}
                              onChange={e => updateEditField('ship_last_name', e.target.value)} />
                          </div>
                          <input className={inputCls} placeholder="Email" value={editForm.ship_email}
                            onChange={e => updateEditField('ship_email', e.target.value)} />
                          <input className={inputCls} placeholder="Phone" value={editForm.ship_phone}
                            onChange={e => updateEditField('ship_phone', e.target.value)} />
                          <input className={inputCls} placeholder="Address" value={editForm.ship_address}
                            onChange={e => updateEditField('ship_address', e.target.value)} />
                          <div className="grid grid-cols-3 gap-2">
                            <input className={inputCls} placeholder="City" value={editForm.ship_city}
                              onChange={e => updateEditField('ship_city', e.target.value)} />
                            <input className={inputCls} placeholder="State" value={editForm.ship_state}
                              onChange={e => updateEditField('ship_state', e.target.value)} />
                            <input className={inputCls} placeholder="Pincode" value={editForm.ship_pincode}
                              onChange={e => updateEditField('ship_pincode', e.target.value)} />
                          </div>
                        </div>
                      ) : (
                        <div className={`space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <p className="font-medium">{order.ship_first_name} {order.ship_last_name}</p>
                          <p className="flex items-start gap-2">
                            <MapPin size={13} className="mt-0.5 shrink-0" />
                            <span>{order.ship_address}, {order.ship_city}, {order.ship_state} - {order.ship_pincode}</span>
                          </p>
                          <p className="flex items-center gap-2"><Phone size={13} className="shrink-0" /> {order.ship_phone}</p>
                          <p className="flex items-center gap-2"><Mail size={13} className="shrink-0" /> {order.ship_email}</p>
                        </div>
                      )}
                    </div>

                    {/* Items */}
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Package size={13} /> Order Items
                      </p>
                      {isEditing && editForm ? (
                        <div className="space-y-2">
                          {editForm.items.map((item, idx) => {
                            const isPickerOpen = pickerOpenIdx === idx;
                            return (
                              <div key={idx} className={`p-2.5 rounded-lg space-y-2 ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                                {/* Product name — clickable to open picker */}
                                <div className="relative" ref={isPickerOpen ? pickerRef : undefined}>
                                  <button type="button" onClick={() => openPicker(idx)}
                                    className={`w-full text-left text-xs font-medium leading-tight px-2.5 py-1.5 rounded-lg border transition-colors flex items-center justify-between gap-1 ${isDark ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500 text-white' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}`}>
                                    <span className="truncate">{item.product_name}</span>
                                    <ChevronDown size={12} className={`shrink-0 transition-transform ${isPickerOpen ? 'rotate-180' : ''}`} />
                                  </button>

                                  {/* Picker dropdown */}
                                  {isPickerOpen && (
                                    <div className={`absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minWidth: 280 }}>
                                      {/* Search */}
                                      <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <div className="relative">
                                          <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                          <input type="text" placeholder="Search products..." value={pickerSearch}
                                            onChange={e => setPickerSearch(e.target.value)} autoFocus
                                            className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
                                        </div>
                                      </div>
                                      {/* Brand tabs */}
                                      <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <button onClick={() => setPickerBrand('all')}
                                          className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${pickerBrand === 'all' ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                                          All
                                        </button>
                                        {brands.map(b => (
                                          <button key={b} onClick={() => setPickerBrand(b)}
                                            className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${pickerBrand === b ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                                            {b}
                                          </button>
                                        ))}
                                      </div>
                                      {/* Product list */}
                                      <div className="max-h-48 overflow-y-auto">
                                        {filteredProducts.length === 0 && (
                                          <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products found</p>
                                        )}
                                        {filteredProducts.map(p => (
                                          <button key={p.id} type="button"
                                            onClick={() => selectProduct(idx, p)}
                                            className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${p.id === item.product_id ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : ''} ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                            <div className="min-w-0">
                                              <p className={`text-xs font-medium truncate ${p.id === item.product_id ? 'text-emerald-500' : ''}`}>{p.name}</p>
                                              <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand} {'\u00B7'} {p.flavor} {'\u00B7'} {p.strength}{p.strength_unit}</p>
                                            </div>
                                            <span className={`text-xs font-bold shrink-0 ${p.id === item.product_id ? 'text-emerald-500' : ''}`}>{fmt(p.price)}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Price + qty + delete */}
                                <div className="flex items-center gap-2">
                                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{fmt(item.unit_price)} each</p>
                                  <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>{'\u00D7'}</span>
                                  <input type="number" min="1"
                                    value={item.quantity || ''}
                                    placeholder="Qty"
                                    onChange={e => updateEditItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                                    className={`w-16 ${inputCls} ${!item.quantity ? 'border-red-400' : ''}`} />
                                  {!item.quantity && (
                                    <span className="text-[10px] text-red-400 shrink-0">Enter qty</span>
                                  )}
                                  <button onClick={() => removeEditItem(idx)} className={`p-1 text-red-400 hover:text-red-300 transition-colors ${!item.quantity ? '' : 'ml-auto'}`}>
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}

                          {/* Add new item button */}
                          <div className="relative" ref={pickerOpenIdx === -1 ? pickerRef : undefined}>
                            <button type="button" onClick={() => openPicker(-1)}
                              className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed text-xs font-medium transition-colors ${isDark ? 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-300' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'}`}>
                              <Plus size={14} /> Add Item
                            </button>
                            {pickerOpenIdx === -1 && (
                              <div className={`absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minWidth: 280 }}>
                                <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                  <div className="relative">
                                    <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                    <input type="text" placeholder="Search products..." value={pickerSearch}
                                      onChange={e => setPickerSearch(e.target.value)} autoFocus
                                      className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
                                  </div>
                                </div>
                                <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                  <button onClick={() => setPickerBrand('all')}
                                    className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${pickerBrand === 'all' ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                                    All
                                  </button>
                                  {brands.map(b => (
                                    <button key={b} onClick={() => setPickerBrand(b)}
                                      className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${pickerBrand === b ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                                      {b}
                                    </button>
                                  ))}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {filteredProducts.length === 0 && (
                                    <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products found</p>
                                  )}
                                  {filteredProducts.map(p => (
                                    <button key={p.id} type="button"
                                      onClick={() => addNewItem(p)}
                                      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                      <div className="min-w-0">
                                        <p className="text-xs font-medium truncate">{p.name}</p>
                                        <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand} {'\u00B7'} {p.flavor} {'\u00B7'} {p.strength}{p.strength_unit}</p>
                                      </div>
                                      <span className="text-xs font-bold shrink-0">{fmt(p.price)}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {order.items?.map(item => (
                            <div key={item.id} className={`flex justify-between text-sm p-2 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                              <div>
                                <p className="font-medium">{item.product_name}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Qty: {item.quantity} {'\u00D7'} {fmt(item.unit_price)}</p>
                              </div>
                              <p className="font-semibold">{fmt(item.unit_price * item.quantity)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Price summary + status */}
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Price Summary</p>
                      {(() => {
                        const itemsSource = isEditing && editForm ? editForm.items : (order.items ?? []);
                        const liveSubtotal = itemsSource.reduce((s, i) => s + i.unit_price * (i.quantity || 0), 0);
                        const liveShipping = liveSubtotal >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
                        const storedDiscount = order.discount_amount ?? (order as any).discountAmount ?? 0;
                        const orderCoupon = order.coupon_code ?? (order as any).couponCode ?? null;
                        // Live recalculate when editing with full coupon data; fall back to stored when loading
                        let effectiveDiscount = storedDiscount;
                        let couponApplicable = storedDiscount > 0;
                        if (isEditing && editForm && orderCoupon) {
                          if (editCouponCache === 'invalid') {
                            effectiveDiscount = 0; couponApplicable = false;
                          } else if (editCouponCache) {
                            if (liveSubtotal >= editCouponCache.minOrderValue) {
                              couponApplicable = true;
                              if (editCouponCache.discountType === 'percentage') {
                                effectiveDiscount = Math.floor(liveSubtotal * editCouponCache.discountValue / 100);
                                if (editCouponCache.maxDiscount) effectiveDiscount = Math.min(effectiveDiscount, editCouponCache.maxDiscount);
                              } else {
                                effectiveDiscount = Math.floor(editCouponCache.discountValue);
                              }
                              effectiveDiscount = Math.min(effectiveDiscount, liveSubtotal);
                            } else {
                              effectiveDiscount = 0; couponApplicable = false;
                            }
                          }
                          // editCouponCache === null → still loading → keep stored values
                        }
                        const liveTotal = liveSubtotal + liveShipping - effectiveDiscount;
                        const displayAmount = couponApplicable ? effectiveDiscount : storedDiscount;
                        return (
                          <div className={`space-y-2 text-sm p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-white'}`}>
                            <div className="flex justify-between">
                              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal</span>
                              <span>{fmt(liveSubtotal)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                              <span>{liveShipping === 0 ? <span className="text-emerald-500 font-medium">FREE</span> : fmt(liveShipping)}</span>
                            </div>
                            {orderCoupon && (
                              <div className={`flex justify-between gap-2 ${couponApplicable ? 'text-emerald-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                <span className={!couponApplicable ? 'line-through' : ''}>
                                  Coupon ({orderCoupon})
                                </span>
                                <span className={`shrink-0 ${!couponApplicable ? 'line-through' : ''}`}>
                                  -{fmt(displayAmount)}
                                  {!couponApplicable && isEditing && (
                                    <span className="ml-1 text-[10px] no-underline" style={{ textDecoration: 'none' }}>removed</span>
                                  )}
                                </span>
                              </div>
                            )}
                            {(order.coin_discount ?? 0) > 0 && (
                              <div className="flex justify-between text-emerald-500">
                                <span>ZS Coins ({order.coins_redeemed})</span>
                                <span className="shrink-0">-{fmt(order.coin_discount ?? 0)}</span>
                              </div>
                            )}
                            <div className={`flex justify-between font-bold pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                              <span>Total</span>
                              <span className="text-emerald-500">{fmt(liveTotal - (order.coin_discount ?? 0))}</span>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="mt-4">
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Update Status</p>
                        <div className="flex flex-wrap gap-1.5">
                          {STATUS_OPTIONS.map(s => {
                            const sCfg = STATUS_CONFIG[s];
                            const SIcon = sCfg.icon;
                            return (
                              <button key={s} onClick={() => updateStatus(order.id, s)}
                                disabled={updatingId === order.id || order.status === s}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${order.status === s ? `${sCfg.bg} ${sCfg.text} ${sCfg.border}` : isDark ? 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-300' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'} ${updatingId === order.id ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <SIcon size={12} />
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Tracking Information */}
                      <div className="mt-4">
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          <Truck size={13} /> Tracking Info
                        </p>
                        {isEditing && editForm ? (
                          <div className="space-y-2">
                            <input className={inputCls} placeholder="Tracking ID (e.g. AWB123456789)"
                              value={editForm.tracking_id}
                              onChange={e => updateEditField('tracking_id', e.target.value)} />
                            <input className={inputCls} placeholder="Partner tracking URL (e.g. https://www.delhivery.com/track/package)"
                              value={editForm.tracking_url}
                              onChange={e => updateEditField('tracking_url', e.target.value)} />
                          </div>
                        ) : (
                          <div className={`text-sm space-y-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {order.tracking_id ? (
                              <>
                                <p>ID: <span className="font-mono font-semibold">{order.tracking_id}</span></p>
                                {order.tracking_url && (
                                  <a href={`${order.tracking_url}${order.tracking_url.includes('?') ? '&' : '?'}tracking_id=${encodeURIComponent(order.tracking_id)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300 text-xs underline">
                                    Track <ExternalLink size={11} />
                                  </a>
                                )}
                              </>
                            ) : (
                              <p className={`text-xs italic ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>No tracking info yet</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Attribution / Source */}
                  {(order.utm_source || order.referrer || order.device_type) && (
                    <div className={`rounded-lg border p-4 ${isDark ? 'bg-gray-800/30 border-gray-800' : 'bg-white border-gray-200'}`}>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <Globe size={13} /> Attribution & Source
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                        {order.utm_source && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Source</p>
                            <p className="text-sm font-medium capitalize">{order.utm_source}</p>
                          </div>
                        )}
                        {order.utm_medium && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Medium</p>
                            <p className="text-sm font-medium capitalize">{order.utm_medium}</p>
                          </div>
                        )}
                        {order.utm_campaign && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Campaign</p>
                            <p className="text-sm font-medium">{order.utm_campaign}</p>
                          </div>
                        )}
                        {order.device_type && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Device</p>
                            <p className="text-sm font-medium flex items-center gap-1.5 capitalize">
                              {order.device_type === 'mobile' ? <Smartphone size={13} /> : order.device_type === 'tablet' ? <Tablet size={13} /> : <Monitor size={13} />}
                              {order.device_type}
                            </p>
                          </div>
                        )}
                        {order.session_page_views != null && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Page Views</p>
                            <p className="text-sm font-medium flex items-center gap-1.5">
                              <Eye size={13} /> {order.session_page_views}
                            </p>
                          </div>
                        )}
                        {order.referrer && (
                          <div className="col-span-2 sm:col-span-3">
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Referrer</p>
                            <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.referrer}</p>
                          </div>
                        )}
                        {order.landing_page && (
                          <div className="col-span-2 sm:col-span-3 md:col-span-2">
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Landing Page</p>
                            <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.landing_page}</p>
                          </div>
                        )}
                        {order.utm_term && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Term</p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.utm_term}</p>
                          </div>
                        )}
                        {order.utm_content && (
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Content</p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{order.utm_content}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className={isDark ? 'border-b border-gray-800/50' : 'border-b border-gray-100'} />
            </div>
          );
        })}
      </div>
      </div>
    </div>

      {/* ── Export to Sheets confirmation dialog ─────────────────── */}
      {exportDialogOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={closeExportDialog}>
          <div
            className={`relative w-full max-w-lg rounded-2xl border shadow-2xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-emerald-500" />
                <h2 className="font-semibold text-base">Export to Google Sheets</h2>
              </div>
              <button onClick={closeExportDialog}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X size={16} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-5 max-h-[66vh] overflow-y-auto">

              {/* Foreign phone warning */}
              {/^\+(?!91)/.test(exportDialogOrder.ship_phone ?? '') && (
                <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
                  <AlertCircle size={15} className="shrink-0 mt-0.5" />
                  <span>Foreign phone number detected: <span className="font-mono font-semibold">{exportDialogOrder.ship_phone}</span>. Only Indian (+91) numbers are supported.</span>
                </div>
              )}

              {/* Past order history banners */}
              {dlgOrderHistory && (dlgOrderHistory.cancelled > 0 || dlgOrderHistory.returned > 0 || dlgOrderHistory.delivered > 0) && (
                <div className="space-y-2">
                  {(dlgOrderHistory.cancelled > 0 || dlgOrderHistory.returned > 0) && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
                      <AlertCircle size={15} className="shrink-0 mt-0.5" />
                      <div className="space-y-0.5">
                        <p>Past returned orders: <span className="font-bold">{dlgOrderHistory.returned}</span></p>
                        <p>Past cancelled orders: <span className="font-bold">{dlgOrderHistory.cancelled}</span></p>
                      </div>
                    </div>
                  )}
                  {(dlgOrderHistory.cancelled > 0 || dlgOrderHistory.returned > 0 || dlgOrderHistory.delivered > 0) && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-green-500/10 border border-green-500/30 text-green-500 text-sm">
                      <CheckCircle size={15} className="shrink-0 mt-0.5" />
                      <p>Past delivered orders: <span className="font-bold">{dlgOrderHistory.delivered}</span></p>
                    </div>
                  )}
                </div>
              )}

              {/* Editable products — top */}
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Products</p>
                <div className="space-y-2">
                  {exportDialogItems.map((item, idx) => {
                    const isPickerOpen = dlgPickerOpenIdx === idx;
                    return (
                      <div key={idx} className={`p-2.5 rounded-lg space-y-2 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                        <div className="relative" ref={isPickerOpen ? dlgPickerRef : undefined}>
                          <button type="button" onClick={() => openDlgPicker(idx)}
                            className={`w-full text-left text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors flex items-center justify-between gap-1 ${isDark ? 'bg-gray-700/50 border-gray-600 hover:border-gray-500 text-white' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
                            <span className="truncate">{item.product_name}</span>
                            <ChevronDown size={12} className={`shrink-0 transition-transform ${isPickerOpen ? 'rotate-180' : ''}`} />
                          </button>
                          {isPickerOpen && (
                            <div className={`absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minWidth: 280 }}>
                              <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                <div className="relative">
                                  <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                  <input type="text" placeholder="Search products..." value={dlgPickerSearch}
                                    onChange={e => setDlgPickerSearch(e.target.value)} autoFocus
                                    className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
                                </div>
                              </div>
                              <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                <button onClick={() => setDlgPickerBrand('all')}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${dlgPickerBrand === 'all' ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>All</button>
                                {brands.map(b => (
                                  <button key={b} onClick={() => setDlgPickerBrand(b)}
                                    className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${dlgPickerBrand === b ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>{b}</button>
                                ))}
                              </div>
                              <div className="max-h-44 overflow-y-auto">
                                {dlgFilteredProducts.length === 0 && (
                                  <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products found</p>
                                )}
                                {dlgFilteredProducts.map(p => (
                                  <button key={p.id} type="button" onClick={() => selectDlgProduct(idx, p)}
                                    className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${p.id === item.product_id ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : ''} ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                                    <div className="min-w-0">
                                      <p className={`text-xs font-medium truncate ${p.id === item.product_id ? 'text-emerald-500' : ''}`}>{p.name}</p>
                                      <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand} {'·'} {p.flavor} {'·'} {p.strength}{p.strength_unit}</p>
                                    </div>
                                    <span className={`text-xs font-bold shrink-0 ${p.id === item.product_id ? 'text-emerald-500' : ''}`}>{fmt(p.price)}</span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{fmt(item.unit_price)} each</p>
                          <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>{'×'}</span>
                          <input type="number" min="1"
                            value={item.quantity || ''}
                            placeholder="Qty"
                            onChange={e => updateDlgItemQty(idx, parseInt(e.target.value) || 0)}
                            className={`w-16 ${inputCls} ${!item.quantity ? 'border-red-400 focus:border-red-400' : ''}`} />
                          {!item.quantity && (
                            <span className="text-[10px] text-red-400 shrink-0">Enter qty</span>
                          )}
                          <button onClick={() => removeDlgItem(idx)} className={`p-1 text-red-400 hover:text-red-300 transition-colors ${!item.quantity ? '' : 'ml-auto'}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Add item */}
                  <div className="relative" ref={dlgPickerOpenIdx === -1 ? dlgPickerRef : undefined}>
                    <button type="button" onClick={() => openDlgPicker(-1)}
                      className={`w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border-2 border-dashed text-xs font-medium transition-colors ${isDark ? 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-300' : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'}`}>
                      <Plus size={14} /> Add Item
                    </button>
                    {dlgPickerOpenIdx === -1 && (
                      <div className={`absolute z-50 top-full left-0 right-0 mt-1 rounded-lg border shadow-xl overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minWidth: 280 }}>
                        <div className={`p-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          <div className="relative">
                            <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                            <input type="text" placeholder="Search products..." value={dlgPickerSearch}
                              onChange={e => setDlgPickerSearch(e.target.value)} autoFocus
                              className={`w-full pl-8 pr-3 py-1.5 rounded-md border text-xs outline-none ${isDark ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
                          </div>
                        </div>
                        <div className={`flex gap-1 px-2 py-1.5 overflow-x-auto border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                          <button onClick={() => setDlgPickerBrand('all')}
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${dlgPickerBrand === 'all' ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>All</button>
                          {brands.map(b => (
                            <button key={b} onClick={() => setDlgPickerBrand(b)}
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap transition-colors ${dlgPickerBrand === b ? 'bg-emerald-500 text-white' : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>{b}</button>
                          ))}
                        </div>
                        <div className="max-h-44 overflow-y-auto">
                          {dlgFilteredProducts.length === 0 && (
                            <p className={`text-xs text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products found</p>
                          )}
                          {dlgFilteredProducts.map(p => (
                            <button key={p.id} type="button" onClick={() => addDlgItem(p)}
                              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                              <div className="min-w-0">
                                <p className="text-xs font-medium truncate">{p.name}</p>
                                <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand} {'·'} {p.flavor} {'·'} {p.strength}{p.strength_unit}</p>
                              </div>
                              <span className="text-xs font-bold shrink-0">{fmt(p.price)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Price breakdown — live-computed from dialog items */}
              {(() => {
                const dlgSubtotal = exportDialogItems.reduce((s, i) => s + i.unit_price * (i.quantity || 0), 0);
                const dlgShipping = dlgSubtotal >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
                const dlgStoredDiscount = exportDialogOrder.discount_amount ?? (exportDialogOrder as any).discountAmount ?? 0;
                const dlgOrderCoupon = exportDialogOrder.coupon_code ?? (exportDialogOrder as any).couponCode ?? null;
                // Live recalculate from coupon formula; fall back to stored while loading
                let dlgEffectiveDiscount = dlgStoredDiscount;
                let dlgCouponApplicable = dlgStoredDiscount > 0;
                if (dlgOrderCoupon) {
                  if (dlgCouponCache === 'invalid') {
                    dlgEffectiveDiscount = 0; dlgCouponApplicable = false;
                  } else if (dlgCouponCache) {
                    if (dlgSubtotal >= dlgCouponCache.minOrderValue) {
                      dlgCouponApplicable = true;
                      if (dlgCouponCache.discountType === 'percentage') {
                        dlgEffectiveDiscount = Math.floor(dlgSubtotal * dlgCouponCache.discountValue / 100);
                        if (dlgCouponCache.maxDiscount) dlgEffectiveDiscount = Math.min(dlgEffectiveDiscount, dlgCouponCache.maxDiscount);
                      } else {
                        dlgEffectiveDiscount = Math.floor(dlgCouponCache.discountValue);
                      }
                      dlgEffectiveDiscount = Math.min(dlgEffectiveDiscount, dlgSubtotal);
                    } else {
                      dlgEffectiveDiscount = 0; dlgCouponApplicable = false;
                    }
                  }
                  // dlgCouponCache === null → still loading → keep stored values
                }
                const dlgDisplayAmount = dlgCouponApplicable ? dlgEffectiveDiscount : dlgStoredDiscount;
                const dlgTotal = dlgSubtotal + dlgShipping - dlgEffectiveDiscount;
                return (
                  <div className={`rounded-xl border px-4 py-3 space-y-2 text-sm ${isDark ? 'border-gray-800 bg-gray-800/30' : 'border-gray-100 bg-gray-50'}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Price Summary</p>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Subtotal</span>
                      <span>{fmt(dlgSubtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Shipping</span>
                      <span>{dlgShipping === 0 ? <span className="text-emerald-500 font-medium">FREE</span> : fmt(dlgShipping)}</span>
                    </div>
                    {dlgOrderCoupon && (
                      <div className={`flex justify-between gap-2 ${dlgCouponApplicable ? 'text-emerald-500' : isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        <span className={!dlgCouponApplicable ? 'line-through' : ''}>
                          Coupon ({dlgOrderCoupon})
                        </span>
                        <span className={`shrink-0 ${!dlgCouponApplicable ? 'line-through' : ''}`}>
                          -{fmt(dlgDisplayAmount)}
                          {!dlgCouponApplicable && (
                            <span className="ml-1 text-[10px]" style={{ textDecoration: 'none' }}>removed</span>
                          )}
                        </span>
                      </div>
                    )}
                    <div className={`flex justify-between font-bold pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <span>Total</span>
                      <span className="text-emerald-500 text-base">{fmt(dlgTotal)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Read-only shipping details */}
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Shipping Details</p>
                <div className={`rounded-xl border p-4 grid grid-cols-2 gap-x-6 gap-y-3 ${isDark ? 'border-gray-800 bg-gray-800/30' : 'border-gray-100 bg-gray-50'}`}>
                  {([
                    ['Name', `${exportDialogOrder.ship_first_name} ${exportDialogOrder.ship_last_name}`.trim()],
                    ['Phone', exportDialogOrder.ship_phone],
                    ['Email', exportDialogOrder.ship_email],
                    ['Pincode', exportDialogOrder.ship_pincode],
                    ['City', exportDialogOrder.ship_city],
                    ['State', exportDialogOrder.ship_state],
                  ] as [string, string][]).map(([label, value]) => (
                    <div key={label}>
                      <p className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{label}</p>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{value || '—'}</p>
                    </div>
                  ))}
                  <div className="col-span-2">
                    <p className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Address</p>
                    <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {[exportDialogOrder.ship_address, exportDialogOrder.ship_city, exportDialogOrder.ship_state, exportDialogOrder.ship_pincode].filter(Boolean).join(', ') || '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Inline error */}
            {exportError && (
              <div className="px-5 pb-3">
                <p className={`text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400`}>{exportError}</p>
              </div>
            )}

            {/* Footer */}
            <div className={`flex items-center justify-end gap-3 px-5 py-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <button onClick={closeExportDialog}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                Cancel
              </button>
              <button onClick={exportToSheets}
                disabled={exportingId === exportDialogOrder.id || exportDialogItems.length === 0 || exportDialogItems.some(i => !i.quantity) || /^\+(?!91)/.test(exportDialogOrder.ship_phone ?? '')}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg font-medium transition-colors flex items-center gap-2">
                {exportingId === exportDialogOrder.id
                  ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Exporting...</>
                  : <><FileSpreadsheet size={14} /> Export to Sheets</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
